import { store } from '@store/index';
import { logout, showSessionTimeoutWarning, restoreAuth, setAuthInitialized } from '@store/slices/authSlice';
import { refreshAccessToken } from './tokenRefresh';
import { clearSession } from './sessionTeardown';
import { AUTH_STORAGE_KEYS } from '../config/auth';

class AuthService {
  private refreshTimer: NodeJS.Timeout | null = null;
  private warningTimer: NodeJS.Timeout | null = null;
  private readonly REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly WARNING_TIME = parseInt(import.meta.env.VITE_SESSION_WARNING_TIME || '5') * 60 * 1000;

  /**
   * Initialize auth service and set up token refresh
   */
  async init() {
    // Try to restore auth state from localStorage
    await this.restoreAuthState();

    const tokenExpiresAt = store.getState().auth.tokenExpiresAt;
    if (tokenExpiresAt) {
      this.scheduleTokenRefresh(tokenExpiresAt);
    }

    // Listen for auth state changes
    store.subscribe(() => {
      const { tokenExpiresAt, isAuthenticated } = store.getState().auth;

      if (isAuthenticated && tokenExpiresAt) {
        this.scheduleTokenRefresh(tokenExpiresAt);
      } else {
        this.clearTimers();
      }
    });

    // Handle tab visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkTokenValidity();
      }
    });

    // Handle multi-tab synchronization
    window.addEventListener('storage', (e) => {
      if (e.key === AUTH_STORAGE_KEYS.token && !e.newValue) {
        // Token was removed in another tab, logout in this tab too
        store.dispatch(logout());
      }
    });
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(expiresAt: string) {
    this.clearTimers();

    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    if (timeUntilExpiry <= 0) {
      // Token already expired
      this.performLogout();
      return;
    }

    // Schedule refresh
    const refreshTime = Math.max(0, timeUntilExpiry - this.REFRESH_BUFFER);
    this.refreshTimer = setTimeout(() => {
      void this.refreshOrLogout();
    }, refreshTime);

    // Schedule warning
    const warningTime = Math.max(0, timeUntilExpiry - this.WARNING_TIME);
    this.warningTimer = setTimeout(() => {
      store.dispatch(showSessionTimeoutWarning());
    }, warningTime);
  }

  /**
   * Refresh the authentication token via the shared `refreshAccessToken`
   * helper (also used by the RTK Query refresh-on-401 base query) and schedule
   * the next proactive refresh.
   *
   * Returns the new access token on success; throws on failure so callers can
   * decide how to react (internal timers tear the session down).
   */
  async refreshToken(): Promise<string> {
    // Delegates to the shared refresh helper (single source of truth — it does
    // the HTTP call, updates localStorage + Redux, and coalesces concurrent
    // callers). We schedule the next proactive refresh afterwards.
    const { token, expiresAt } = await refreshAccessToken();
    this.scheduleTokenRefresh(expiresAt);
    return token;
  }

  /**
   * Refresh the token, tearing the session down if the refresh fails. Shared by
   * the scheduled-refresh timer, the tab-visibility check, and manual session
   * extension.
   */
  private async refreshOrLogout() {
    try {
      await this.refreshToken();
    } catch {
      this.performLogout();
    }
  }

  /**
   * Check if token is still valid (used when tab becomes visible)
   */
  async checkTokenValidity() {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.token);
    const { tokenExpiresAt } = store.getState().auth;

    if (!token || !tokenExpiresAt) {
      return;
    }

    const expiryTime = new Date(tokenExpiresAt).getTime();
    const now = Date.now();

    if (now >= expiryTime) {
      // Token expired while tab was hidden
      await this.refreshOrLogout();
    }
  }

  /**
   * Perform logout and cleanup
   */
  performLogout() {
    this.clearTimers();
    clearSession();
  }

  /**
   * Clear all timers
   */
  private clearTimers() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Extend the current session
   */
  async extendSession() {
    await this.refreshOrLogout();
  }

  /**
   * Restore auth state from localStorage on app init
   */
  private async restoreAuthState() {
    console.log('[AuthService] Attempting to restore auth state...');
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.token);
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    const storedTokenExpiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);

    console.log('[AuthService] Found tokens:', {
      hasToken: !!token,
      hasRefreshToken: !!refreshToken,
      hasExpiresAt: !!storedTokenExpiresAt,
      hasUser: !!storedUser
    });

    if (!token || !refreshToken) {
      // No tokens found, mark as initialized
      store.dispatch(setAuthInitialized());
      return;
    }

    try {
      let user;

      // Try to use stored user data first
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
          console.log('[AuthService] Using stored user data:', user);
        } catch (e) {
          console.warn('[AuthService] Failed to parse stored user data:', e);
        }
      }

      // If no stored user data, try to extract from JWT token
      if (!user) {
        console.log('[AuthService] No stored user data, attempting to decode JWT token...');
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error('[AuthService] Invalid token format. Parts:', tokenParts.length);
          throw new Error('Invalid token format');
        }

        // Decode the payload (middle part)
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('[AuthService] Token payload:', payload);

        // Check if token is expired
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          throw new Error('Token expired');
        }

        // Extract minimal user info from token
        user = {
          id: payload.id || '1',
          username: payload.sub,
          email: payload.email || '',
          role: payload.role || 'admin',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }

      // Use stored expiry time if available, otherwise estimate
      let tokenExpiresAt = storedTokenExpiresAt;
      if (!tokenExpiresAt) {
        const sessionDuration = parseInt(import.meta.env.VITE_SESSION_DURATION || '3600') * 1000; // Default 1 hour
        tokenExpiresAt = new Date(Date.now() + sessionDuration).toISOString();
      }

      // Restore auth state in Redux
      console.log('[AuthService] Restoring auth state to Redux store...');
      store.dispatch(restoreAuth({
        user: user as any, // Type assertion needed for compatibility
        token,
        refreshToken,
        tokenExpiresAt,
      }));

      console.log('[AuthService] Auth state restored successfully');

      // Check if token needs refresh (but don't force it immediately)
      const expiryTime = new Date(tokenExpiresAt).getTime();
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      // Only refresh if token expires in less than 5 minutes
      if (timeUntilExpiry < this.REFRESH_BUFFER) {
        console.log('[AuthService] Token expires soon, refreshing...');
        await this.refreshToken();
      }
    } catch (error) {
      console.error('[AuthService] Failed to restore auth state:', error);
      // Token is invalid, clear storage
      localStorage.removeItem(AUTH_STORAGE_KEYS.token);
      localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
      localStorage.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
      localStorage.removeItem(AUTH_STORAGE_KEYS.user);
      // Mark as initialized even if restoration failed
      store.dispatch(setAuthInitialized());
    }
  }
}

export default new AuthService();
